import React, { useEffect, useState } from "react"
import {
  DataGrid, GridColDef, GridSelectionModel, GridToolbarContainer, GridToolbarFilterButton, GridToolbarDensitySelector, GridToolbarColumnsButton, GridSortModel
} from "@mui/x-data-grid"
import { RiExpandUpDownLine } from "react-icons/ri"
import { Box, FormControl, Grid, IconButton, MenuItem, Select, SelectChangeEvent, } from "@mui/material"
import useAuth from "../../context/AuthProvider"
import moment from "moment"
import Swal from "sweetalert2"
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import formatPrice from "../../helper/currencyFormatter"
import { StateReportsBillingModel } from "../../models/StateReportsBillingModel"
import { StateReportModel_Resp } from "../../models/StateReportModel"
import { EditStateReport } from "../AddEdit/EditStateReport"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { apiClient } from "../../helper/api"

interface Iprops {
  listData: StateReportsBillingModel[]
  setItemList: React.Dispatch<React.SetStateAction<StateReportsBillingModel[]>>
  setFilterModel: any
  setSortModel: any
  sortModel: GridSortModel
  selectedMonth: string
  selectedYear: string
  selectedTab: number
  pageSize: number
  setPageSize: React.Dispatch<React.SetStateAction<number>>
  currentPage: number
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  totalCount: number
  selectedPartner: string
  fetchStateReportBillingData: (pageSize: number, currentPage: number) => Promise<StateReportsBillingModel[]>
}

export const StateReportBillingList: React.FC<Iprops> = ({
  listData,
  setItemList,
  setFilterModel,
  setSortModel,
  sortModel,
  selectedMonth,
  selectedYear,
  selectedTab,
  pageSize,
  setPageSize,
  currentPage,
  setCurrentPage,
  totalCount,
  selectedPartner,
  fetchStateReportBillingData
}) => {
  const { user } = useAuth()
  const [selectedRows, setSelectedRows] = useState<StateReportsBillingModel[]>()
  const [OpenEdit, setOpenEdit] = React.useState(false);

  const onRowsSelectionHandler = (ids: GridSelectionModel) => {
    const selectedIDs = new Set(ids)
    const selectedRowData = listData.filter(
      (row) => selectedIDs.has(row?.rowNum ?? "")
    )
    setSelectedRows(selectedRowData)
  }

  const initialLBData: StateReportModel_Resp = {
    stateReportModel_Respdata: [],
    largeReport: undefined,
    completedPage: undefined,
    totalCount: 0,
    pageCount: 0,
    pageSize: 25,
    currentPage: 0

  };

  useEffect(() => {
    setSelectedRows([])
  }, [selectedTab])

  const handleEdit = (ILobData: StateReportsBillingModel) => {
    setFormData(ILobData);
    setOpenEdit(true);
  };

  const handleClose = () => {
    setOpenEdit(false);
  };

  const handleSubmitAndRefresh = async () => {
    // Refresh the data first, then close the dialog
    const refreshedData = await fetchStateReportBillingData(pageSize, currentPage);
    setItemList(refreshedData);
    setOpenEdit(false);
  };

  const initialFormData: StateReportsBillingModel = {
    rowNum: '',
    scheduleId: 0,
    stateReportId: 0,
    stateName: '',
    licensee: '',
    reportName: '',
    dueDate: '',
    period: '',
    payment: false,
    paymentType: '',
    mainLicensee: false,
    reportRequired: false,
    stateId: '',
    license: '',
    dateFinal: '',
    isPaymentRequired: false,
    isReportRequired: false,
    licenseMain: false,
    premiumReport: false,
    feeDue: '',
    partnerName: '',
    state: '',
    largeReport: false,
    largeReportFee: ''
  };
  const [Formdata, setFormData] = React.useState<StateReportsBillingModel>(initialFormData);

  const printDataExcel = async () => {
    // Fix issue #2: Remove pagination limit to get all records
    let dataToPrint = selectedRows && selectedRows.length > 0 ? selectedRows : await fetchStateReportBillingData(10000, 1);
    let grandTotalFeeDue = 0;
    try {
      const workbook = new ExcelJS.Workbook();
      const response = await fetch('/assets/brand/StateReportBillingTemplate.xlsx');
      const arrayBuffer = await response.arrayBuffer();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.getWorksheet(1);
      if (!worksheet) return alert("Something went wrong");

      // Clear the template content and start fresh
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber <= 10) { // Clear first 10 rows to remove logo and template content
          row.eachCell((cell, colNumber) => {
            cell.value = null;
            cell.fill = { type: 'pattern', pattern: 'none' };
            cell.font = {};
            cell.border = {};
          });
        }
      });

      let rowIndex = 1; // Start from row 1 since we cleared the template
      let filteredColumns = Columns.filter(col => col.headerName && col.headerName !== 'No' && col.headerName !== 'startDate' && col.headerName !== 'endDate')
      if (selectedTab === 1) {
        filteredColumns = filteredColumns.filter(col => 
          col.headerName !== 'Date Completed' && 
          col.headerName !== 'Large Report' &&
          col.headerName !== 'First Request' &&
          col.headerName !== 'Second Request' &&
          col.headerName !== 'Third Request' && 
          col.headerName !== 'NotesToClient' &&
          col.headerName !== 'Tax Reported' && 
          col.headerName !== 'Fee Due' &&
          col.headerName !== 'Main Licensee'
        );
      }
      if (selectedTab === 2) {
        filteredColumns = filteredColumns.filter(col => 
          col.headerName !== 'First Request' &&
          col.headerName !== 'Second Request' &&
          col.headerName !== 'Third Request' && 
          col.headerName !== 'Wet Signature Required' &&
          col.headerName !== 'Notary Stamp Required'
        );
      }
      if (selectedTab === 3) {
        filteredColumns = filteredColumns.filter(col => 
          col.headerName !== 'Date Completed' &&  
          col.headerName !== 'Wet Signature Required' &&
          col.headerName !== 'Notary Stamp Required'
        );
      }
      // Fix issue #1: Set correct title based on selected tab
      const getReportTitle = () => {
        switch (selectedTab) {
          case 1: return 'Upcoming State Reports and Payments';
          case 2: return 'Completed State Reports and Payments';
          case 3: return 'Outstanding State Reports and Payments';
          default: return 'State Reports and Payments';
        }
      };

      // Fix issue #3: Remove state grouping, show transactions in filter order
      const stateColumnIndex = filteredColumns.findIndex(col => col.headerName === 'state');
      if (stateColumnIndex !== -1) filteredColumns.unshift(filteredColumns.splice(stateColumnIndex, 1)[0]);
      const columnHeaders = filteredColumns.map(col => col.headerName);

      const addHeaderRow = (rowIdx: number) => {
        const headerRow = worksheet.getRow(rowIdx);
        columnHeaders.forEach((header, colIndex) => {
          if (header !== "Edit") {
            const cell = headerRow.getCell(colIndex + 1);
            cell.value = header;
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '808080' } }; // Header row in solid grey}
          }
        });
        return rowIdx + 1;
      };

      // Get company name or "All" if no specific partner selected
      const getCompanyName = () => {
        if (selectedPartner === '0' || !selectedPartner) {
          return 'All';
        }
        // Find the partner name from the data
        const firstRow = dataToPrint[0];
        return firstRow?.partnerName || 'All';
      };

      // Set the report title (no "Filed Transactions" - just the correct title)
      worksheet.getRow(rowIndex).getCell(2).value = getReportTitle();
      worksheet.getRow(rowIndex).getCell(2).font = { bold: true, size: 16 };
      rowIndex++;

      // Set the company name
      worksheet.getRow(rowIndex).getCell(2).value = `Company: ${getCompanyName()}`;
      worksheet.getRow(rowIndex).getCell(2).font = { bold: true, size: 14 };
      rowIndex += 2;

      // Add header row
      rowIndex = addHeaderRow(rowIndex);

      // Process all data in order (no grouping by client or state)
      let colorCycleIndex = 0;
      dataToPrint.forEach((row) => {
        const newFilteredColumns = filteredColumns.filter((val) => val.field != "stateRule")
        newFilteredColumns.forEach((col, colIndex) => {
          if (col.field == "largeReport" || col.field == "largeReportFee") {
            delete col.hide;
          }
          if (selectedTab == 1) {
            if (col.field == "notaryStamp" || col.field == "wetSignature"){
              delete col.hide;
            }  
          }
          if (!col.hide) {
            let value = (row as any)[col.field];
            if (col.field === "payment" || col.field === "reportRequired" || col.field === "mainLicensee" || col.field ==='notaryStamp' || col.field ==='wetSignature') value = value ? "true" : "false";
            else if (col.field === 'dateFinalReportSentToClient') value = value ? moment(value).format("MM/DD/YYYY") : '';
            else if (col.field == 'taxReported' || col.field == 'taxAmount' || col.field == 'largeReportFee') value = formatPrice(value);
            else if (col.field == 'largeReport') value = String(value).toLowerCase() === 'true' || value === true ? 'Yes' : 'No';
            else value = value !== undefined && value !== null ? value : "";

            if (col.field === 'feeDue') {
              const fee = parseFloat(value) || 0;
              grandTotalFeeDue += fee;
            }
            
            // Add Large Report Fee to total if it exists
            if (col.field === 'largeReportFee' && value) {
              const largeReportFee = parseFloat(
                typeof value === 'string'
                  ? value.replace(/\$/g, '').replace(/,/g, '')
                  : (value || '').toString().replace(/\$/g, '').replace(/,/g, '')
              ) || 0;
              grandTotalFeeDue += largeReportFee;
            }

            const cell = worksheet.getRow(rowIndex).getCell(colIndex + 1);

            cell.value = (col.field === "feeDue") ? Number(value) : value;
            if (col.field === "feeDue") cell.numFmt = '"$"#,##0.00';
            // Apply alternating fill colors: white, light gray, dark gray
            const fillColors = ['FFFFFF', 'ededed', 'd9d9d9'];
            const fillColor = fillColors[colorCycleIndex % 3];
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
          }
        });

        rowIndex++;
        colorCycleIndex++; // Increment to apply the next color in the cycle
      });
      // if (selectedTab == 1){
      //   worksheet.getCell(`I${rowIndex}`).value = 'Total Fee Due';
      //   worksheet.getCell(`I${rowIndex}`).font = { bold: true, size: 15, color: { argb: "FF0000" } };
      //   worksheet.getCell(`J${rowIndex}`).value = grandTotalFeeDue;
      //   worksheet.getCell(`J${rowIndex}`).numFmt = '"$"#,##0.00';
      //   worksheet.getCell(`J${rowIndex}`).font = { bold: true, size: 15, color: { argb: "FF0000" } };
      // }
      // else
       if (selectedTab == 3){
        worksheet.getCell(`O${rowIndex}`).value = 'Total Fee Due';
        worksheet.getCell(`O${rowIndex}`).font = { bold: true, size: 15, color: { argb: "FF0000" } };
        worksheet.getCell(`P${rowIndex}`).value = grandTotalFeeDue;
        worksheet.getCell(`P${rowIndex}`).numFmt = '"$"#,##0.00';
        worksheet.getCell(`P${rowIndex}`).font = { bold: true, size: 15, color: { argb: "FF0000" } };
      }
      else if(selectedTab == 2) {
      worksheet.getCell(`M${rowIndex}`).value = 'Total Fee Due';
      worksheet.getCell(`M${rowIndex}`).font = { bold: true, size: 15, color: { argb: "FF0000" } };
      worksheet.getCell(`N${rowIndex}`).value = grandTotalFeeDue;
      worksheet.getCell(`N${rowIndex}`).numFmt = '"$"#,##0.00';
      worksheet.getCell(`N${rowIndex}`).font = { bold: true, size: 15, color: { argb: "FF0000" } };
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, 'StateReport.xlsx');
    } catch (error) {
      console.error("Error generating the Excel file:", error);
    }
  };

  const printDataPdf = async () => {
    console.log('🖨️ STATE REPORTS BILLING PDF PRINT CLICKED - Starting PDF generation...');
    
    // State Reports Billing: Use server-side PDF generation
    try {
      const token = user?.token;
      
      // Log user context and current state
      console.log('🔍 USER CONTEXT:', {
        userId: user?.userId,
        userRoles: user?.userRoles,
        token: token ? 'Present' : 'Missing'
      });
      
      console.log('🔍 CURRENT UI STATE:', {
        selectedPartner: selectedPartner,
        selectedMonth: selectedMonth,
        selectedYear: selectedYear,
        selectedTab: selectedTab,
        tabName: selectedTab === 1 ? 'Upcoming' : selectedTab === 2 ? 'Completed' : selectedTab === 3 ? 'Outstanding' : 'Unknown',
        totalCount: totalCount,
        selectedRowsCount: selectedRows?.length || 0,
        hasSelectedRows: selectedRows && selectedRows.length > 0
      });
      
      // Show loading dialog
      Swal.fire({
        title: 'Generating PDF...',
        text: 'Please wait while we generate your State Reports PDF.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Use the dedicated PDF endpoint for all tabs
      const endpoint = '/StateReport/getStateReportBillingPdf';
      
      // Build request payload for the dedicated PDF endpoint
      const requestPayload = {
        PartnerId: selectedPartner || '',
        selectedTab: selectedTab,
        pageSize: 1000,
        currentPage: 1,
        field: 'dueDate',
        sort: 'asc',
        fieldName: '',
        value: '',
        operator: '',
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // Use month/year for all tabs (no date range)
        useDateRange: false,
        month: selectedMonth,
        year: selectedYear
      };
      
      // Build the comprehensive request object for logging
      const fullRequestObject = {
        url: endpoint,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: requestPayload
      };

      // Log the complete request with full authorization token for debugging
      console.log('=== STATE REPORTS BILLING PDF REQUEST DEBUG ===');
      console.log('🔍 ENDPOINT:', fullRequestObject.url);
      console.log('🔍 METHOD:', fullRequestObject.method);
      console.log('🔍 HEADERS:', JSON.stringify(fullRequestObject.headers, null, 2));
      console.log('🔍 FULL AUTHORIZATION TOKEN:', token);
      console.log('🔍 REQUEST BODY:', JSON.stringify(fullRequestObject.body, null, 2));
      console.log('🔍 REQUEST SIZE:', JSON.stringify(fullRequestObject.body).length + ' characters');
      console.log('🔍 READY-TO-USE cURL COMMAND:');
      console.log(`curl -X POST "${window.location.origin}${fullRequestObject.url}" \\`);
      console.log(`  -H "Content-Type: application/json" \\`);
      console.log(`  -H "Authorization: Bearer ${token}" \\`);
      console.log(`  -d '${JSON.stringify(fullRequestObject.body)}'`);
      console.log('=== END REQUEST LOG ===');

      const responseJson = await apiClient(endpoint, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        }),
        body: JSON.stringify(requestPayload),
      });

      Swal.close();

      // Log the full response for debugging
      console.log('=== STATE REPORTS BILLING PDF API RESPONSE DEBUG ===');
      console.log('📥 FULL RESPONSE OBJECT:', responseJson);
      console.log('📥 RESPONSE TYPE:', typeof responseJson);
      console.log('📥 RESPONSE KEYS:', responseJson ? Object.keys(responseJson) : 'No response');
      console.log('📥 RESPONSE STATUS INDICATORS:');
      console.log('   - responseJson.status:', responseJson?.status);
      console.log('   - responseJson.success:', responseJson?.success);
      console.log('   - responseJson.data:', responseJson?.data);
      console.log('   - responseJson.message:', responseJson?.message);
      console.log('   - responseJson.error:', responseJson?.error);
      if (responseJson?.data) {
        console.log('📥 RESPONSE DATA ANALYSIS:');
        console.log('   - data type:', typeof responseJson.data);
        console.log('   - data keys:', Object.keys(responseJson.data));
        console.log('   - pdf_url present:', !!responseJson.data.pdf_url);
        console.log('   - filename present:', !!responseJson.data.filename);
        console.log('   - pdf_url value:', responseJson.data.pdf_url);
        console.log('   - filename value:', responseJson.data.filename);
        console.log('   - original_data_count:', responseJson.data.original_data_count);
      }
      console.log('=== END RESPONSE DEBUG ===');

      if (!responseJson) {
        console.log('❌ ERROR: No response received from server');
        Swal.fire({
          title: 'Error!',
          text: 'Failed to get response from server.',
          icon: 'error',
          confirmButtonColor: '#02A4E3'
        });
        return;
      }

      // Handle server-side PDF generation response
      if (responseJson.status && responseJson.data && responseJson.data.pdf_url) {
        // Download PDF from the provided URL
        const link = document.createElement('a');
        link.href = responseJson.data.pdf_url;
        link.download = responseJson.data.filename || 'state-reports-billing.pdf';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Show success message
        Swal.fire({
          title: 'Success!',
          text: `PDF downloaded successfully! ${responseJson.data.original_data_count || 0} records processed.`,
          icon: 'success',
          confirmButtonColor: '#02A4E3',
          timer: 3000,
          timerProgressBar: true
        });
      } else {
        // API did not return expected response
        console.error('❌ API Response Error: Expected pdf_url in response');
        Swal.fire({
          title: 'Error!',
          text: 'Failed to generate PDF. Please try again or contact support.',
          icon: 'error',
          confirmButtonColor: '#02A4E3'
        });
      }
    } catch (error) {
      console.error("❌ Server-side PDF generation failed:", error);
      Swal.close();
      Swal.fire({
        title: 'Error!',
        text: 'An error occurred while generating the PDF. Please try again.',
        icon: 'error',
        confirmButtonColor: '#02A4E3'
      });
    }
  };


  const printData = (option: string) => {
    console.log('📋 STATE REPORTS BILLING PRINT OPTION SELECTED:', {
      option: option,
      currentPage: window.location.href,
      selectedTab: selectedTab,
      tabName: selectedTab === 1 ? 'Upcoming' : selectedTab === 2 ? 'Completed' : selectedTab === 3 ? 'Outstanding' : 'Unknown',
      selectedRowsCount: selectedRows?.length || 0,
      totalDataCount: totalCount,
      selectedPartner: selectedPartner,
      selectedMonth: selectedMonth,
      selectedYear: selectedYear
    });
    
    if (option === 'excel') {
      printDataExcel();
    } else if (option === 'pdf') {
      printDataPdf();
    } else {
      console.log('⚠️ WARNING: Invalid print option selected:', option);
      Swal.fire({
        icon: 'warning',
        title: 'No option selected',
        text: 'Please select an option to proceed.',
      });
    }
  };

  function EditToolbar() {
    return (
      <React.Fragment>
        <Box display="flex" sx={{}}>
          <Grid
            container
            display="flex"
            margin={"10px 0px"}
            justifyContent={"space-between"}
          >
            <Grid item xs={12} md={6}>
              <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarDensitySelector />
                {/* Removed GridToolbarExport since we have our own Print functionality */}
              </GridToolbarContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container display='flex' justifyContent={'flex-end'}>
                <Grid item xs={4} sm={4} md={1.5} minWidth={130} className="select-input-border">
                    <FormControl fullWidth className="">
                      <Select
                        onChange={(event: SelectChangeEvent<string>) => {
                          printData(event.target.value);
                        }}
                        id="test-select-label"
                        autoWidth
                        size="small"
                        label='Print'
                        className="active-btn-print"
                        IconComponent={ExpandMoreIcon}
                        displayEmpty
                        style={{ margin: 0 }}
                        value={''}
                        renderValue={(selected) => {
                          return 'Print';
                        }}
                      >
                        <MenuItem value={'excel'}>Excel</MenuItem>
                        {selectedPartner !== '0' && (
                          <MenuItem value={'pdf'}>PDF</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <EditStateReport handleClose={handleClose} Open={OpenEdit} StateRuleMatrixCL={Formdata} handleSubmit={handleSubmitAndRefresh} initValues={initialLBData} isStateReport={false} />
      </React.Fragment>
    );
  }

  const Columns: GridColDef[] = [
    {
      field: 'paymentFilingScheduleId',
      type: 'number',
      headerName: 'No',
      hide: true,
      hideable: false,
      disableExport: true,
    },
    {
      field: "stateName",
      type: "text",
      headerName: "State",
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
    },
    {
      field: "reportName",
      type: "text",
      headerName: "Report Name",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
    },
    {
      field: "period",
      type: "text",
      headerName: "Period",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
    },
    {
      field: "dueDate",
      type: "text",
      headerName: "Due Date",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) =>
        moment(params.value).format("MM/DD/YYYY"),
    },
    {
      field: "paymentType",
      type: "text",
      headerName: "Payment Type",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
    },
    {
      field: "licensee",
      type: "text",
      headerName: "Licensee",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
    },
    ((selectedTab === 1 || selectedTab === 3)? {
      field: "dateFinalReportSentToClient",
      type: "text",
      headerName: "Date Completed",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      hide: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      renderCell: (params) => {
        const dateValue = params.value;
        return dateValue ? moment(dateValue).format("MM/DD/YYYY") : "";
      },
      } : 
      {
        field: "dateFinalReportSentToClient",
        type: "text",
        headerName: "Date Completed",
        minWidth: 200,
        headerAlign: "center",
        align: "center",
        hideSortIcons: true,
        renderHeader: (params) => (
          <div>
            <span className="table-card-title">{params.colDef.headerName}</span>
            <RiExpandUpDownLine />
          </div>
        ),
        renderCell: (params) => {
          const dateValue = params.value;
          return dateValue ? moment(dateValue).format("MM/DD/YYYY") : "";
        },
      }
    ),
    {
      field: "payment",
      type: "text",
      headerName: "Payment Required",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
    },
    {
      field: "reportRequired",
      type: "text",
      headerName: "Report Required",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
      editable: true, // Make the feeDue column editable
    },
    {
      field: "mainLicensee",
      type: "text",
      headerName: "Main Licensee",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
      editable: true, // Make the feeDue column editable
    },
    (selectedTab === 3 ?
      {
        field: "firstRequest",
        type: "text",
        headerName: "First Request",
        minWidth: 200,
        headerAlign: "center",
        align: "center",
        hideSortIcons: true,
        renderHeader: (params) => (
          <div>
            <span className="table-card-title">{params.colDef.headerName}</span>
            <RiExpandUpDownLine />
          </div>
        ),
        valueGetter: (params) => {
          return params.value ? moment(params.value).format("MM/DD/YYYY") : "";
        }
      } :       
      {
      field: "firstRequest",
      type: "text",
      headerName: "First Request",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      hide: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => {
        return params.value ? moment(params.value).format("MM/DD/YYYY") : "";
      }
      
    } ),
    (selectedTab === 3 ?
      {
        field: "secondRequest",
        type: "text",
        headerName: "Second Request",
        minWidth: 200,
        headerAlign: "center",
        align: "center",
        hideSortIcons: true,
        renderHeader: (params) => (
          <div>
            <span className="table-card-title">{params.colDef.headerName}</span>
            <RiExpandUpDownLine />
          </div>
        ),
        valueGetter: (params) => {
          return params.value ? moment(params.value).format("MM/DD/YYYY") : "";
        }
        
      } :       
      {
      field: "secondRequest",
      type: "text",
      headerName: "Second Request",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      hide: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => {
        return params.value ? moment(params.value).format("MM/DD/YYYY") : "";
      }
      
    } ),
    (selectedTab === 3 ?
      {
        field: "thirdRequest",
        type: "text",
        headerName: "Third Request",
        minWidth: 200,
        headerAlign: "center",
        align: "center",
        hideSortIcons: true,
        renderHeader: (params) => (
          <div>
            <span className="table-card-title">{params.colDef.headerName}</span>
            <RiExpandUpDownLine />
          </div>
        ),
        valueGetter: (params) => {
          return params.value ? moment(params.value).format("MM/DD/YYYY") : "";
        }
        
      } :       
      {
      field: "thirdRequest",
      type: "text",
      headerName: "Third Request",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      hide: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => {
        return params.value ? moment(params.value).format("MM/DD/YYYY") : "";
      }
      
    } ),
    (selectedTab === 1 ?
    {
      field: "notes",
      type: "text",
      headerName: "NotesToClient",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      hide: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value
    } : 
    {
      field: "notes",
      type: "text",
      headerName: "Notes To Client",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value
    }),
    (selectedTab === 1 ?
      {
        field: "taxAmount",
        type: "text",
        headerName: "Tax Reported",
        minWidth: 200,
        headerAlign: "center",
        align: "center",
        hideSortIcons: true,
        hide: true,
        renderHeader: (params) => (
          <div>
            <span className="table-card-title">{params.colDef.headerName}</span>
            <RiExpandUpDownLine />
          </div>
        ),
        valueGetter: (params) => formatPrice(params.value),
        editable: true, // Make the feeDue column editable
      } : 
      {
        field: "taxAmount",
        type: "text",
        headerName: "Tax Reported",
        minWidth: 200,
        headerAlign: "center",
        align: "center",
        hideSortIcons: true,
        renderHeader: (params) => (
          <div>
            <span className="table-card-title">{params.colDef.headerName}</span>
            <RiExpandUpDownLine />
          </div>
        ),
        valueGetter: (params) => formatPrice(params.value),
        editable: true, // Make the feeDue column editable
      } ),
    (selectedTab === 1 ?
    {
      field: "largeReport",
      type: "text",
      headerName: "Large Report",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      hide: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === '') {
          return (
            <Box display="flex" alignItems="center" justifyContent="center" style={{
              color: '#999', 
              fontStyle: 'italic'
            }}>
              N/A
            </Box>
          );
        }
        
        // Handle various boolean representations from API
        const isTrue = String(value).toLowerCase() === 'true' || value === true || value === 1;
        
        return (
          <Box display="flex" alignItems="center" justifyContent="center">
            {isTrue ? 'Yes' : 'No'}
          </Box>
        );
      },
      editable: false,
    } :     
    {
      field: "largeReport",
      type: "text",
      headerName: "Large Report",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      renderCell: (params) => {
        const value = params.value;
        if (value === null || value === undefined || value === '') {
          return (
            <Box display="flex" alignItems="center" justifyContent="center" style={{
              color: '#999', 
              fontStyle: 'italic'
            }}>
              N/A
            </Box>
          );
        }
        
        // Handle various boolean representations from API
        const isTrue = String(value).toLowerCase() === 'true' || value === true || value === 1;
        
        return (
          <Box display="flex" alignItems="center" justifyContent="center">
            {isTrue ? 'Yes' : 'No'}
          </Box>
        );
      },
      editable: false,
    }),
    // Large Report Fee column - only show when there's a fee value
    {
      field: "largeReportFee",
      type: "text",
      headerName: "Large Report Fee",
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      renderCell: (params) => {
        const value = params.value;
        if (!value || value === 0 || value === '0' || value === '') {
          return (
            <Box display="flex" alignItems="center" justifyContent="center" style={{
              color: '#999', 
              fontStyle: 'italic'
            }}>
              -
            </Box>
          );
        }
        
        return (
          <Box display="flex" alignItems="center" justifyContent="center">
            {formatPrice(value)}
          </Box>
        );
      },
      editable: false,
    }, 
    {
      field: "wetSignature",
      type: "text",
      headerName: "Wet Signature Required",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      hide: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
      editable: true, // Make the feeDue column editable
    },
    {
      field: "notaryStamp",
      type: "text",
      headerName: "Notary Stamp Required",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      hide: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) => params.value,
      editable: true, // Make the feeDue column editable
    },
    {
      field: "feeDue",
      type: "text",
      headerName: "Fee Due",
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      hideSortIcons: true,
      renderHeader: (params) => (
        <div>
          <span className="table-card-title">{params.colDef.headerName}</span>
          <RiExpandUpDownLine />
        </div>
      ),
      valueGetter: (params) =>
        formatPrice(typeof params.value === 'string' ? params.value : String(params.value)),
      editable: true, // Make the feeDue column editable
    },
    {
      field: 'stateRule',
      flex: 0.4,
      headerName: "Edit",
      hideSortIcons: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <div>
          <IconButton
            color="primary"
            onClick={() => {
              handleEdit(params.row);
            }}
            disabled={false}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17 3C17.2626 2.73735 17.5744 2.52901 17.9176 2.38687C18.2608 2.24473 18.6286 2.17157 19 2.17157C19.3714 2.17157 19.7392 2.24473 20.0824 2.38687C20.4256 2.52901 20.7374 2.73735 21 3C21.2626 3.26264 21.471 3.57444 21.6131 3.9176C21.7553 4.26077 21.8284 4.62856 21.8284 5C21.8284 5.37143 21.7553 5.73923 21.6131 6.08239C21.471 6.42555 21.2626 6.73735 21 7L7.5 20.5L2 22L3.5 16.5L17 3Z"
                stroke={String(params.row.largeReport).toLowerCase() === 'true' ? "#02A4E3" : "#B0B0B0"} // Gray color when false - handle string booleans
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </IconButton>
        </div>
      ),
    }

  ];

  const handleRowEditCommit = (row: any) => {
    let updatedRow = row.row
    let AllRec = [...listData];
    let exsistinfRec = AllRec.find(x => x.rowNum === updatedRow.rowNum);
    if (exsistinfRec != undefined) {
      exsistinfRec.feeDue = updatedRow.feeDue;
      setItemList(AllRec);
    }

    return updatedRow;
  };

  return (
    <>
      <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
      </div>
      <div style={{ height: '60vh', width: '100%', overflow: 'auto' }}>
        <DataGrid
          getRowId={(r) => r.rowNum}
          rows={listData}
          columns={Columns}
          pageSize={pageSize}
          pagination
          rowCount={totalCount || 0}
          paginationMode="server"
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          page={currentPage - 1}
          onPageChange={(newPage) => setCurrentPage(newPage + 1)}
          rowsPerPageOptions={[10, 25, 50]}
          editMode="row"
          onRowEditStop={handleRowEditCommit}
          checkboxSelection={
            user?.userRoles.split(",").includes("Super Admin") ? true : false
          }
          filterMode="server"
          onFilterModelChange={setFilterModel} // For filtering
          sortingMode="server"
          sortingOrder={['asc', 'desc']}
          sortModel={sortModel} // Show current sort state
          onSortModelChange={setSortModel} // Add this line for sorting
          selectionModel={selectedRows?.map((item) => item.rowNum).filter((rowNum): rowNum is Exclude<typeof rowNum, undefined> => rowNum !== undefined) || []}
          onSelectionModelChange={
            user?.userRoles.split(",").includes("Super Admin")
              ? (newRowSelectionModel) => {
                onRowsSelectionHandler(newRowSelectionModel)
              }
              : undefined
          }
          components={{
            Toolbar: EditToolbar,
          }}
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: "normal",
            },
            fontFamily: "'Proxima Nova', sans-serif",
            border: "0px",
            fontSize: "16px",
          }}
        />
      </div>
    </>
  )
}
